import { Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { SponsorStrip } from "./SponsorStrip";

/**
 * Layout for public-facing pages.
 *
 * Motion:
 *  - Outlet fades on route change (existing, AnimatePresence mode="wait")
 *  - A thin race-red line sweeps across the very top of the viewport on
 *    each navigation. Under 180ms so it never delays perceived nav.
 */
export function PublicLayout() {
  const location = useLocation();
  const reduce = useReducedMotion();

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/*
        * Skip link — the first focusable element on every page. Hidden until it
        * receives keyboard focus, then it jumps focus past the header nav to the
        * main content (WCAG 2.4.1 Bypass Blocks).
        */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Skip to main content
      </a>
      {/*
        * Page-transition race line — sweeps in, holds for a beat, then fades
        * out within ~600 ms total. Self-completing keyframe animation so the
        * line never lingers at the top of the viewport between route changes
        * (the earlier AnimatePresence implementation left it stuck at scaleX 1
        * indefinitely, which read as a persistent stray bar).
        *
        * Re-keyed by pathname so React recreates the node and restarts the
        * animation on every route change.
        */}
      {!reduce && (
        <motion.div
          key={`race-line-${location.pathname}`}
          aria-hidden
          className="pointer-events-none fixed inset-x-0 top-0 z-50 h-[3px] origin-left bg-gradient-to-r from-transparent via-primary to-transparent"
          initial={{ scaleX: 0, opacity: 1 }}
          animate={{ scaleX: [0, 1, 1, 1], opacity: [1, 1, 1, 0] }}
          transition={{
            duration: 0.6,
            times: [0, 0.3, 0.7, 1],
            ease: "easeOut"
          }}
        />
      )}

      <Header />
      <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
      {/* Self-gating: renders only on the public marketing routes. */}
      <SponsorStrip />
      <Footer />
    </div>
  );
}
